import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import {
  type GraphViewPreviewable,
  type SnapshotId,
  date,
} from '@ipfabric/utils';
import classNames from 'classnames';
import { useLocale } from 'containers/App/LocaleContext';
import type { FC } from 'react';
import type { MouseEvent } from 'react';
import { useDateTimeFormat } from 'utils/hooks/useDateTimeFormat';
import { useTimezone } from 'utils/hooks/useTimeZone';
import { useHasPermission } from 'utils/permissions/useHasPermission';
import {
  getBreadcrumbsShortLabel,
  getTextBreadcrumbs,
} from '../GraphBreadcrumbs/helpers/breadcrumbsText';
import { useGraphBreadcrumbs } from '../GraphBreadcrumbs/useGraphBreadcrumbs';

import { Button } from 'components/Button/Button';

interface Props {
  deleting: boolean;
  favorite?: boolean;
  isViewByUser: (view: GraphViewPreviewable) => boolean;
  onDeleteClick: (view: GraphViewPreviewable) => void;
  onStarredToggle: (view: GraphViewPreviewable) => void;
  onViewClick: (view: GraphViewPreviewable) => void;
  snapshotId: SnapshotId;
  setPreview: (preview: string | null) => void;
  starToggling: boolean;
  view: GraphViewPreviewable;
}

export const GraphLoadViewItem: FC<Props> = (props) => {
  const dateTimeFormat = useDateTimeFormat();
  const timezone = useTimezone();
  const msg = useLocale();
  const hasPermission = useHasPermission();

  const breadcrumbs = useGraphBreadcrumbs(
    props.view.parameters,
    props.snapshotId,
    props.view.overlay ?? null,
    null,
  );

  const breadcrumbsLabel = getBreadcrumbsShortLabel(props.view.parameters, msg);
  const breadcrumbsTooltip = getTextBreadcrumbs(breadcrumbs, msg);

  return (
    <div
      className={classNames(
        'cursor-pointer',
        'overflow-hidden',
        'relative',
        'flex',
        'items-center',
        'graph-load-view__item',
      )}
      onClick={() => {
        props.onViewClick(props.view);
      }}
      onMouseOver={() => {
        props.setPreview(props.view.preview);
      }}
      onMouseLeave={() => {
        props.setPreview(null);
      }}
    >
      <div className='flex-1 overflow-hidden'>
        <div className='font-bold'>{props.view.name}</div>
        <div className='text-xs font-semibold'>
          {breadcrumbsLabel}
          <Tooltip content={breadcrumbsTooltip} placement='left'>
            <i className='fa fa-fw fa-question-circle ml-lgu-2' />
          </Tooltip>
        </div>
        <div className='text-xs'>
          {date.formatUtcTimestampInTimeZone(
            props.view.savedAt,
            timezone,
            dateTimeFormat,
          )}
        </div>
      </div>
      <div className='flex'>
        <Button
          variant='tertiary'
          size='xxxs'
          loading={props.starToggling}
          icon='fa-star'
          iconStyle={props.favorite ? 'solid' : 'regular'}
          onClick={(event: MouseEvent) => {
            event.stopPropagation();
            if (!props.starToggling) {
              props.onStarredToggle(props.view);
            }
          }}
          className={classNames(
            props.starToggling ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
          type='button'
        />
        {(props.isViewByUser(props.view) || hasPermission('settings')) && (
          <>
            <span className='graph-load-view__item-separator' />
            <Button
              variant='tertiary'
              size='xxxs'
              onClick={(event: MouseEvent) => {
                event.stopPropagation();
                if (!props.deleting) {
                  props.onDeleteClick(props.view);
                }
              }}
              loading={props.deleting}
              icon='fa-trash-can'
              className='m-1'
              type='button'
            />
          </>
        )}
      </div>
    </div>
  );
};
